import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { ParentPortalDashboard, ParentPortalUpgradeGate } from "@/components/parent-portal";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ParentPortalPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  usePushNotifications();

  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        const response = await axios.get(`${API}/parent-communication/parent/me`);
        setPortalData(response.data);
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || "Unable to load parent portal";
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchPortalData();
  }, []);

  const handleUpgradeInterest = async () => {
    setUpgradeLoading(true);
    try {
      await axios.post(`${API}/parent-communication/parent/upgrade-interest`);
      toast.success("Your center has been notified of your interest.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save your interest");
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/parent/login", { replace: true });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f6fbfa]" data-testid="parent-portal-loading-state">Loading parent portal…</div>;
  }

  if (!portalData) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f6fbfa]" data-testid="parent-portal-empty-state">Parent portal unavailable.</div>;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_34%),#f6fbfa] px-4 py-8" data-testid="parent-portal-page">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="flex justify-end">
          <Button variant="outline" className="rounded-full" onClick={handleLogout} data-testid="parent-portal-logout-button">Log out</Button>
        </div>

        {portalData.upgradeRequired ? (
          <ParentPortalUpgradeGate portalData={portalData} onUpgradeInterest={handleUpgradeInterest} upgradeLoading={upgradeLoading} />
        ) : (
          <ParentPortalDashboard portalData={portalData} />
        )}
      </div>
    </div>
  );
}
